import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { type Components } from "react-markdown";
import { FindingSeverity, QCFinding } from "../../types/qcTypes";
import styles from "./QCResults.module.css";
import {
  FindingActionType,
  severityColor,
  severityLabel,
  useQcComponents,
} from "./qcRedlineShared";

// The findings view shared by both QC flows: the toolbar (counts, severity
// summary, bulk actions), the inline redline content, and the Details / Edit
// popups. IndependentQCResultsView and PanelQCResultsView differ only in how
// they render the content (markdown only, or HTML or markdown), which they
// pass in as `renderContent`.

export interface QCFindingsViewProps {
  findings: QCFinding[];
  onAcceptFinding: (
    findingId: number,
    editedText?: string | null,
  ) => void | Promise<void>;
  onDeclineFinding: (findingId: number) => void | Promise<void>;
  onRevertFinding?: (findingId: number) => void | Promise<void>;
  onAcceptAll: () => void | Promise<void>;
  onDeclineAll: () => void | Promise<void>;
  onRevertAll?: () => void | Promise<void>;
  onSubmitFeedback?: (
    findingId: number,
    helpful: boolean,
  ) => void | Promise<void>;
  readOnly?: boolean;
  // When true, render headings at their native level (document mode).
  preserveHeadings?: boolean;
  renderContent: (components: Components, className: string) => React.ReactNode;
}

const SEVERITY_ORDER: FindingSeverity[] = [
  "critical",
  "major",
  "minor",
  "info",
];

type Anchor = { element: HTMLElement; finding: QCFinding } | null;

// Joy's Menu doesn't close on an outside click outside a Dropdown context,
// so close a popup manually when the press lands outside it and its anchor.
const useCloseOnOutsidePress = (
  anchor: Anchor,
  contentRef: React.RefObject<HTMLDivElement>,
  close: () => void,
) => {
  useEffect(() => {
    if (!anchor) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (anchor.element.contains(target)) return;
      if (contentRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [anchor, contentRef, close]);
};

const QCFindingsView: React.FC<QCFindingsViewProps> = ({
  findings,
  onAcceptFinding,
  onDeclineFinding,
  onRevertFinding,
  onAcceptAll,
  onDeclineAll,
  onRevertAll,
  onSubmitFeedback,
  readOnly = false,
  preserveHeadings = false,
  renderContent,
}) => {
  const [detailsAnchor, setDetailsAnchor] = useState<Anchor>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const [editAnchor, setEditAnchor] = useState<Anchor>(null);
  const editRef = useRef<HTMLDivElement>(null);
  const [editDraft, setEditDraft] = useState<string>("");

  const closeDetails = useMemo(() => () => setDetailsAnchor(null), []);
  const closeEdit = useMemo(() => () => setEditAnchor(null), []);
  useCloseOnOutsidePress(detailsAnchor, detailsRef, closeDetails);
  useCloseOnOutsidePress(editAnchor, editRef, closeEdit);

  const [loadingAction, setLoadingAction] = useState<{
    findingId: number;
    action: FindingActionType;
  } | null>(null);

  const isLoading = (findingId: number, action: FindingActionType) =>
    loadingAction?.findingId === findingId && loadingAction?.action === action;
  const isAnyLoadingFor = (findingId: number) =>
    loadingAction?.findingId === findingId;

  const runAction = async (
    findingId: number,
    action: FindingActionType,
    fn: () => void | Promise<void>,
  ) => {
    if (loadingAction) return;
    setLoadingAction({ findingId, action });
    try {
      await Promise.resolve(fn());
    } finally {
      setLoadingAction(null);
    }
  };

  const pendingCount = findings.filter((f) => f.Resolution === null).length;
  const acceptedCount = findings.filter(
    (f) => f.Resolution === "accepted",
  ).length;
  const declinedCount = findings.filter(
    (f) => f.Resolution === "declined",
  ).length;
  const severityCounts = SEVERITY_ORDER.map((severity) => ({
    severity,
    count: findings.filter((f) => f.Severity === severity).length,
  })).filter(({ count }) => count > 0);

  const findingsById = useMemo(() => {
    const map = new Map<string, QCFinding>();
    for (const f of findings) map.set(String(f.FindingID), f);
    return map;
  }, [findings]);

  // The popup shows the live finding, so feedback given there updates it.
  const detailsFinding = detailsAnchor
    ? (findingsById.get(String(detailsAnchor.finding.FindingID)) ??
      detailsAnchor.finding)
    : null;

  const handleDetails = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    finding: QCFinding,
  ) => {
    const target = event.currentTarget;
    setDetailsAnchor((prev) =>
      prev && prev.finding.FindingID === finding.FindingID
        ? null
        : { element: target, finding },
    );
  };

  const closeDetailsFor = (findingId: number) => {
    setDetailsAnchor((prev) =>
      prev && prev.finding.FindingID === findingId ? null : prev,
    );
  };

  const handleAccept = (findingId: number) => {
    closeDetailsFor(findingId);
    runAction(findingId, "accept", () => onAcceptFinding(findingId));
  };

  const handleOpenEdit = (
    event: React.MouseEvent<HTMLElement>,
    finding: QCFinding,
  ) => {
    closeDetailsFor(finding.FindingID);
    const target = event.currentTarget;
    setEditDraft(finding.EditedText ?? finding.SuggestedText ?? "");
    setEditAnchor({ element: target, finding });
  };

  const handleSaveEdit = () => {
    if (!editAnchor) return;
    const { finding } = editAnchor;
    const draft = editDraft;
    setEditAnchor(null);
    runAction(finding.FindingID, "accept", () =>
      onAcceptFinding(finding.FindingID, draft),
    );
  };

  const handleDecline = (findingId: number) => {
    closeDetailsFor(findingId);
    runAction(findingId, "decline", () => onDeclineFinding(findingId));
  };

  const handleRevert = (findingId: number) => {
    if (!onRevertFinding) return;
    closeDetailsFor(findingId);
    runAction(findingId, "revert", () => onRevertFinding(findingId));
  };

  const handleFeedback = (findingId: number, helpful: boolean) => {
    if (!onSubmitFeedback) return;
    runAction(findingId, helpful ? "feedback-up" : "feedback-down", () =>
      onSubmitFeedback(findingId, helpful),
    );
  };

  type BulkAction = "accept-all" | "decline-all" | "revert-all";
  const [bulkLoading, setBulkLoading] = useState<BulkAction | null>(null);

  const runBulk = async (
    action: BulkAction,
    fn: () => void | Promise<void>,
  ) => {
    if (bulkLoading) return;
    setBulkLoading(action);
    try {
      await Promise.resolve(fn());
    } finally {
      setBulkLoading(null);
    }
  };

  const components = useQcComponents({
    styles,
    findingsById,
    readOnly,
    preserveHeadings,
    loadingAction,
    isLoading,
    isAnyLoadingFor,
    onAccept: handleAccept,
    onDecline: handleDecline,
    onOpenEdit: handleOpenEdit,
    onDetails: handleDetails,
    onRevert: handleRevert,
    onRevertFinding,
  });

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
      >
        <Typography level="body-sm">
          {findings.length} finding{findings.length !== 1 ? "s" : ""}
        </Typography>
        {severityCounts.map(({ severity, count }) => (
          <Chip
            key={severity}
            size="sm"
            variant="outlined"
            color={severityColor[severity] as "danger"}
          >
            {count} {severityLabel[severity].toLowerCase()}
          </Chip>
        ))}
        {pendingCount > 0 && (
          <Chip size="sm" variant="soft" color="neutral">
            {pendingCount} pending
          </Chip>
        )}
        {acceptedCount > 0 && (
          <Chip size="sm" variant="soft" color="success">
            {acceptedCount} accepted
          </Chip>
        )}
        {declinedCount > 0 && (
          <Chip size="sm" variant="soft" color="danger">
            {declinedCount} declined
          </Chip>
        )}
        {!readOnly && findings.length > 0 && (
          <Stack direction="row" spacing={1}>
            {acceptedCount < findings.length && (
              <Button
                size="sm"
                variant="outlined"
                color="success"
                loading={bulkLoading === "accept-all"}
                disabled={bulkLoading !== null && bulkLoading !== "accept-all"}
                onClick={() => runBulk("accept-all", () => onAcceptAll())}
              >
                Accept all
              </Button>
            )}
            {declinedCount < findings.length && (
              <Button
                size="sm"
                variant="outlined"
                color="danger"
                loading={bulkLoading === "decline-all"}
                disabled={bulkLoading !== null && bulkLoading !== "decline-all"}
                onClick={() => runBulk("decline-all", () => onDeclineAll())}
              >
                Decline all
              </Button>
            )}
            {onRevertAll && acceptedCount + declinedCount > 0 && (
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                loading={bulkLoading === "revert-all"}
                disabled={bulkLoading !== null && bulkLoading !== "revert-all"}
                onClick={() => runBulk("revert-all", () => onRevertAll())}
              >
                Revert all
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {renderContent(components, styles.redlineContent)}

      {/* Details popup (Joy has no Popover; a Menu restyled as a dialog) */}
      <Menu
        open={Boolean(detailsAnchor)}
        anchorEl={detailsAnchor?.element}
        onClose={closeDetails}
        placement="bottom-start"
        role="dialog"
        aria-label="Finding details"
        sx={{ p: 0, zIndex: 1400 }}
      >
        {detailsFinding && (
          <Box ref={detailsRef} sx={{ p: 2, maxWidth: 400 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="sm"
                  color={severityColor[detailsFinding.Severity] as "danger"}
                >
                  {severityLabel[detailsFinding.Severity]}
                </Chip>
                <Typography
                  level="body-xs"
                  sx={{ textTransform: "capitalize" }}
                >
                  {detailsFinding.FindingType.replace("_", " ")}
                </Typography>
              </Stack>
              <Typography level="body-sm">
                {detailsFinding.Explanation}
              </Typography>
              {detailsFinding.SourceName && (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "sm",
                    bgcolor: "background.level1",
                  }}
                >
                  <Typography level="body-xs" fontWeight="lg">
                    Source: {detailsFinding.SourceName}
                  </Typography>
                  {detailsFinding.SourceSnippet && (
                    <Typography
                      level="body-xs"
                      sx={{ mt: 0.5, fontStyle: "italic" }}
                    >
                      “{detailsFinding.SourceSnippet}”
                    </Typography>
                  )}
                </Box>
              )}
              {/* Feedback lives here rather than inline after every decision. */}
              {!readOnly &&
                onSubmitFeedback &&
                detailsFinding.Resolution !== null && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {detailsFinding.Helpful === null ? (
                      <>
                        <Typography level="body-xs" sx={{ mr: 0.5 }}>
                          Was this finding helpful?
                        </Typography>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="success"
                          aria-label="Yes, this was helpful"
                          loading={isLoading(
                            detailsFinding.FindingID,
                            "feedback-up",
                          )}
                          disabled={isAnyLoadingFor(detailsFinding.FindingID)}
                          onClick={() =>
                            handleFeedback(detailsFinding.FindingID, true)
                          }
                        >
                          <ThumbUpIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          aria-label="No, this was not helpful"
                          loading={isLoading(
                            detailsFinding.FindingID,
                            "feedback-down",
                          )}
                          disabled={isAnyLoadingFor(detailsFinding.FindingID)}
                          onClick={() =>
                            handleFeedback(detailsFinding.FindingID, false)
                          }
                        >
                          <ThumbDownIcon />
                        </IconButton>
                      </>
                    ) : (
                      <Typography
                        level="body-xs"
                        color={detailsFinding.Helpful ? "success" : "neutral"}
                      >
                        {detailsFinding.Helpful
                          ? "You marked this helpful"
                          : "You marked this not helpful"}
                      </Typography>
                    )}
                  </Stack>
                )}
            </Stack>
          </Box>
        )}
      </Menu>

      {/* Edit replacement popup */}
      <Menu
        open={Boolean(editAnchor)}
        anchorEl={editAnchor?.element}
        onClose={closeEdit}
        placement="bottom-start"
        role="dialog"
        aria-label="Edit replacement"
        sx={{ p: 0, zIndex: 1400 }}
      >
        {editAnchor?.finding && (
          <Box ref={editRef} sx={{ p: 2, width: 420, maxWidth: "90vw" }}>
            <Stack spacing={1.5}>
              <Typography level="title-sm">
                {editAnchor.finding.SuggestedText
                  ? "Edit replacement"
                  : "Write replacement"}
              </Typography>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "sm",
                  bgcolor: "background.level1",
                }}
              >
                <Typography
                  level="body-xs"
                  sx={{ color: "text.tertiary", mb: 0.5 }}
                >
                  Original
                </Typography>
                <Typography
                  level="body-sm"
                  sx={{ textDecoration: "line-through" }}
                >
                  {editAnchor.finding.OriginalText}
                </Typography>
              </Box>
              <Textarea
                autoFocus
                minRows={2}
                maxRows={8}
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                slotProps={{ textarea: { "aria-label": "Replacement text" } }}
                placeholder={
                  editAnchor.finding.SuggestedText
                    ? undefined
                    : "Type a replacement, or leave empty to delete the original…"
                }
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    handleSaveEdit();
                  }
                }}
              />
              {editDraft.length === 0 && (
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Leaving this empty will delete the original text.
                </Typography>
              )}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={closeEdit}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="solid"
                  color={editDraft.length === 0 ? "danger" : "primary"}
                  onClick={handleSaveEdit}
                >
                  {editDraft.length === 0
                    ? "Accept as deletion"
                    : "Save and accept"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default QCFindingsView;
